// src/pages/Kyc.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { submitKyc, getKycStatus } from "../redux/slices/kycSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Shield,
  Upload,
  Camera,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  CreditCard,
  Car,
} from "lucide-react";
import toast from "react-hot-toast";

// Type for document types
type DocumentType = "passport" | "id_card" | "driver_license";

const Kyc: React.FC = () => {
  const dispatch = useDispatch();
  const { kyc, isLoading } = useSelector((state: RootState) => state.kyc);
  const { user } = useSelector((state: RootState) => state.auth);
  const [documentType, setDocumentType] = useState<DocumentType>("passport");
  const [documentFront, setDocumentFront] = useState<File | null>(null);
  const [documentBack, setDocumentBack] = useState<File | null>(null);
  const [selfiePhoto, setSelfiePhoto] = useState<File | null>(null);
  const [previewFront, setPreviewFront] = useState<string>("");
  const [previewBack, setPreviewBack] = useState<string>("");
  const [previewSelfie, setPreviewSelfie] = useState<string>("");

  useEffect(() => {
    dispatch(getKycStatus() as any);
  }, [dispatch]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "front" | "back" | "selfie"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "front") {
        setDocumentFront(file);
        setPreviewFront(reader.result as string);
      } else if (type === "back") {
        setDocumentBack(file);
        setPreviewBack(reader.result as string);
      } else {
        setSelfiePhoto(file);
        setPreviewSelfie(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!documentFront || !selfiePhoto) {
      toast.error("Please upload all required documents");
      return;
    }

    // Check if document back is required (for ID card and driver license)
    const requiresBack =
      documentType === "id_card" || documentType === "driver_license";
    if (requiresBack && !documentBack) {
      toast.error("Please upload both sides of your document");
      return;
    }

    const formData = new FormData();
    formData.append("document_type", documentType);
    formData.append("document_front", documentFront);
    if (documentBack) formData.append("document_back", documentBack);
    formData.append("selfie_photo", selfiePhoto);

    try {
      await dispatch(submitKyc(formData) as any);
      toast.success("KYC submitted successfully!");

      // Reset form
      setDocumentFront(null);
      setDocumentBack(null);
      setSelfiePhoto(null);
      setPreviewFront("");
      setPreviewBack("");
      setPreviewSelfie("");
      setDocumentType("passport");
    } catch (error) {
      toast.error("Failed to submit KYC");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "rejected":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "pending":
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    if (!status) return "Unknown";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getDocumentTypeIcon = (type: DocumentType) => {
    switch (type) {
      case "passport":
        return <FileText className="h-5 w-5" />;
      case "id_card":
        return <CreditCard className="h-5 w-5" />;
      case "driver_license":
        return <Car className="h-5 w-5" />;
    }
  };

  const requiresBackDocument =
    documentType === "id_card" || documentType === "driver_license";

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <h1 className={GlobalStyles.heading1}>KYC Verification</h1>
          <p className={GlobalStyles.bodyText + " mt-2"}>
            Complete your identity verification to access all platform features
          </p>
        </div>

        {/* KYC Status */}
        {kyc && kyc.status && (
          <div
            className={`${GlobalStyles.card} ${GlobalStyles.sectionSpacing}`}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                {getStatusIcon(kyc.status)}
                <div className="ml-3">
                  <h3 className={GlobalStyles.heading3}>
                    KYC Status: {getStatusText(kyc.status)}
                  </h3>
                  {kyc.submitted_at && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Submitted on{" "}
                      {new Date(kyc.submitted_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <span
                className={`${GlobalStyles.badge} ${
                  kyc.status === "approved"
                    ? GlobalStyles.badgeSuccess
                    : kyc.status === "pending"
                    ? GlobalStyles.badgeWarning
                    : kyc.status === "rejected"
                    ? GlobalStyles.badgeError
                    : ""
                }`}
              >
                {kyc.status}
              </span>
            </div>

            {kyc.admin_notes && (
              <div
                className={`${GlobalStyles.alert} ${
                  kyc.status === "rejected"
                    ? GlobalStyles.alertError
                    : GlobalStyles.alertInfo
                }`}
              >
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">
                    {kyc.status === "rejected"
                      ? "Reason for rejection:"
                      : "Admin notes:"}
                  </p>
                  <p className="text-sm mt-1">{kyc.admin_notes}</p>
                </div>
              </div>
            )}

            {kyc.status === "pending" && (
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-4`}
              >
                <Clock className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">
                  Your KYC verification is under review. This process usually
                  takes 24-48 hours.
                </p>
              </div>
            )}
          </div>
        )}

        {/* KYC Form - Show if no KYC or if KYC was rejected */}
        {(!kyc || !kyc.status || kyc.status === "rejected") && (
          <div className={GlobalStyles.card}>
            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h3 className={GlobalStyles.heading2 + " mb-2"}>
                Identity Verification
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Please provide the following documents for verification
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Document Type */}
              <div>
                <h4 className={`${GlobalStyles.heading3} mb-4`}>
                  Select Document Type
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(
                    ["passport", "id_card", "driver_license"] as DocumentType[]
                  ).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setDocumentType(type)}
                      className={`p-4 rounded-lg border flex flex-col items-center justify-center transition-colors ${
                        documentType === type
                          ? "border-primary bg-primary/5"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                      }`}
                    >
                      {getDocumentTypeIcon(type)}
                      <span className="mt-2 font-medium capitalize">
                        {type.replace("_", " ")}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Document Front */}
              <div>
                <h4 className={`${GlobalStyles.heading3} mb-4`}>
                  Document Front
                </h4>
                <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-8 hover:border-primary transition-colors">
                  {previewFront ? (
                    <div className="relative">
                      <img
                        src={previewFront}
                        alt="Document Front"
                        className="w-full h-auto rounded-lg max-h-64 object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setDocumentFront(null);
                          setPreviewFront("");
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center cursor-pointer">
                      <Upload className="h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 dark:text-gray-400 mb-2">
                        Upload clear photo of document front
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        JPG, PNG (Max 5MB)
                      </p>
                      <div
                        className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                      >
                        Select File
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, "front")}
                          required
                        />
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Document Back (only for ID card and driver license) */}
              {requiresBackDocument && (
                <div>
                  <h4 className={`${GlobalStyles.heading3} mb-4`}>
                    Document Back
                  </h4>
                  <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-8 hover:border-primary transition-colors">
                    {previewBack ? (
                      <div className="relative">
                        <img
                          src={previewBack}
                          alt="Document Back"
                          className="w-full h-auto rounded-lg max-h-64 object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setDocumentBack(null);
                            setPreviewBack("");
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center cursor-pointer">
                        <Upload className="h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-gray-600 dark:text-gray-400 mb-2">
                          Upload clear photo of document back
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                          JPG, PNG (Max 5MB)
                        </p>
                        <div
                          className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                        >
                          Select File
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileChange(e, "back")}
                            required={requiresBackDocument}
                          />
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              )}

              {/* Selfie Photo */}
              <div>
                <h4 className={`${GlobalStyles.heading3} mb-4`}>
                  Selfie with Document
                </h4>
                <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-8 hover:border-primary transition-colors">
                  {previewSelfie ? (
                    <div className="relative">
                      <img
                        src={previewSelfie}
                        alt="Selfie"
                        className="w-full h-auto rounded-lg max-h-64 object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setSelfiePhoto(null);
                          setPreviewSelfie("");
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center cursor-pointer">
                      <Camera className="h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 dark:text-gray-400 mb-2">
                        Take a selfie while holding your document
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Face and document must be clearly visible
                      </p>
                      <div
                        className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                      >
                        Take Photo
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, "selfie")}
                          capture="environment"
                          required
                        />
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Requirements */}
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo}`}
              >
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">
                    Verification Requirements:
                  </p>
                  <ul className="text-sm mt-2 space-y-1">
                    <li>• All documents must be valid and not expired</li>
                    <li>• Photos must be clear and all text readable</li>
                    <li>
                      • Selfie must clearly show your face and the document
                    </li>
                    <li>• All information must match your account details</li>
                  </ul>
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-5 h-5 mr-2`}
                      ></div>
                      Submitting...
                    </div>
                  ) : (
                    <>
                      <Shield className="h-5 w-5 mr-2" />
                      Submit for Verification
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* KYC Benefits - Show if user needs to complete KYC */}
        {user?.verification_level === 1 &&
          (!kyc || !kyc.status || kyc.status === "rejected") && (
            <div className={`${GlobalStyles.card} mt-8`}>
              <h3 className={`${GlobalStyles.heading3} mb-6`}>
                Benefits of KYC Verification
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Higher Limits
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Increased deposit and withdrawal limits
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-3">
                    <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Enhanced Security
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Added protection for your account
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mb-3">
                    <Shield className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Full Access
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Access to all platform features including recovery services
                  </p>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default Kyc;
